import React from 'react';
import {
  AnomalyDirection,
  Phase0Row,
  SeasonAnomalyHit,
  SeasonAnomalyProduct,
} from '../../types/AiForecasting';
import {
  peak_arrow,
  peak_direction,
  peak_pct,
  peak_vs_reg_label,
  peak_vs_reg_tip,
} from './SkuBreakdown.peak';
import { other_anomalies_title, other_anomaly_summary } from './SkuBreakdown.others';
import './SkuBreakdown.css';

const BREAKDOWN_TIPS: Record<string, string> = {
  prior_year:
    'Cases sold in the 12 months before this trailing year.',
  this_year:
    'Cases sold in the last 12 months through today. Always a full year, even early in the calendar year.',
  yoy: 'How the last 12 months compare with the 12 months before that.',
  holiday_prior:
    'Cases sold in this holiday the year before the last time this SKU had the season.',
  last_holiday: 'Cases sold in this holiday the last year the season occurred for this SKU.',
  holiday_n:
    'How this holiday’s sales changed versus the year before. Shown for context. It does not change how many cases to make.',
  need: 'Expected holiday cases: last holiday × (1 + trailing YoY%). Holiday n% is not used.',
  regular:
    'Typical month outside this holiday (this year minus last holiday, divided by 12). Extra months of this rate are added when shelf life allows a larger, less frequent run.',
  on_hand: 'Cases already in inventory that have not expired.',
  to_make:
    'To make = holiday need + regular month × (usable months − 1) − on hand. Usable months are shelf life minus the days that must still be left at the holiday. Never below zero.',
  window: 'The date range when this SKU can be made for the next holiday, based on shelf life. Latest is the day before the holiday. Earliest still leaves remaining life when the holiday ends.',
};

function Tip({
  label,
  tip,
  children,
}: {
  label?: string;
  tip: string;
  children?: React.ReactNode;
}) {
  return (
    <span className="forecast-tip" tabIndex={0}>
      {children || label}
      <span className="forecast-tip__bubble" role="tooltip">
        {tip}
      </span>
    </span>
  );
}

function expiration_tip(row: Phase0Row): string {
  if (row.shelf_life_days != null && row.shelf_life_days > 0) {
    return `Usable cover from BMS shelf life (${row.shelf_life_days} days) minus ${row.remaining_life_days} days that must still be left at the holiday. That is ${row.expiration_months} months. Extra regular months = ${Math.max(0, row.expiration_months - 1)}.`;
  }
  return `BMS has no shelf life for this product, so we use ${row.expiration_months} usable months and still reserve ${row.remaining_life_days} days at the holiday.`;
}

export function SeasonChangeBadge({ row }: { row: SeasonAnomalyProduct }) {
  return (
    <span className={`forecast-anomaly__change forecast-anomaly__change--${peak_direction(row)}`}>
      {peak_vs_reg_label(row)}
    </span>
  );
}

function format_range(start?: string | null, end?: string | null): string | undefined {
  if (!start || !end) {
    return undefined;
  }
  return `${start} – ${end}`;
}

function format_pct(value: number | null): string {
  if (value == null) {
    return '—';
  }
  return `${value}%`;
}

function format_cases(value: number | null): string {
  if (value == null) {
    return '—';
  }
  return String(value);
}

function hit_direction(pct: number): AnomalyDirection {
  if (pct > 0) {
    return 'up';
  }
  if (pct < 0) {
    return 'down';
  }
  return 'flat';
}

export function SkuOtherAnomalies({
  hits,
  current_holiday_label,
  sku_label,
}: {
  hits: SeasonAnomalyHit[];
  current_holiday_label: string;
  sku_label?: string;
}) {
  const who = sku_label || 'this SKU';
  return (
    <div className="forecast-breakdown__others">
      <h4 className="forecast-breakdown__others-title">{other_anomalies_title(sku_label)}</h4>
      <p className="forecast-breakdown__others-lead">
        Holiday weeks besides {current_holiday_label} that differ from {who}’s weeks with no
        holiday, newest first.
      </p>
      {hits.length === 0 ? (
        <p className="forecast-breakdown__others-empty">
          No other holiday weeks look different from {who}’s regular weeks.
        </p>
      ) : (
        <ul className="forecast-breakdown__others-list">
          {hits.map((hit) => {
            const direction = hit_direction(hit.peak_vs_reg_pct);
            return (
              <li key={`${hit.holiday_key}-${hit.year}-${hit.holiday_start}`}>
                <div className="forecast-breakdown__others-head">
                  <strong>
                    {hit.holiday_label} {hit.year}
                  </strong>
                  <span className={`forecast-anomaly__change forecast-anomaly__change--${direction}`}>
                    {peak_arrow(direction)} {Math.abs(hit.peak_vs_reg_pct)}% vs regular weeks
                  </span>
                </div>
                <span className="forecast-breakdown__sub">{other_anomaly_summary(hit)}</span>
                <p className="forecast-breakdown__others-why">{hit.why}</p>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default function SkuBreakdown({
  row,
  holiday_label,
  change_row,
}: {
  row: Phase0Row;
  holiday_label: string;
  change_row?: SeasonAnomalyProduct | null;
}) {
  const items: { key: string; label: string; value: string; sub?: string; tip: string }[] = [
    ...(change_row
      ? [
          {
            key: 'peak',
            label: 'Peak % vs reg week',
            value: `${peak_arrow(peak_direction(change_row))} ${Math.abs(peak_pct(change_row))}%`,
            tip: peak_vs_reg_tip(change_row, holiday_label),
          },
        ]
      : []),
    {
      key: 'prior',
      label: 'Prior year',
      value: format_cases(row.year_prior_sales),
      sub: format_range(row.year_prior_start, row.year_prior_end),
      tip: BREAKDOWN_TIPS.prior_year,
    },
    {
      key: 'this',
      label: 'This year',
      value: format_cases(row.year_latest_sales),
      sub: format_range(row.year_latest_start, row.year_latest_end),
      tip: BREAKDOWN_TIPS.this_year,
    },
    {
      key: 'yoy',
      label: 'YoY %',
      value: format_pct(row.year_change_pct),
      tip: BREAKDOWN_TIPS.yoy,
    },
    {
      key: 'h1',
      label: 'Holiday −1',
      value: format_cases(row.holiday_prior_sales),
      sub: `${row.holiday_label} ${row.holiday_prior_year ?? ''}`.trim(),
      tip: BREAKDOWN_TIPS.holiday_prior,
    },
    {
      key: 'h0',
      label: 'Last holiday',
      value: format_cases(row.holiday_latest_sales),
      sub: `${row.holiday_label} ${row.holiday_latest_year ?? ''}`.trim(),
      tip: BREAKDOWN_TIPS.last_holiday,
    },
    {
      key: 'hn',
      label: 'Holiday n%',
      value: format_pct(row.holiday_n_pct),
      tip: BREAKDOWN_TIPS.holiday_n,
    },
    {
      key: 'need',
      label: 'Holiday need',
      value: format_cases(row.next_need_cases),
      sub: 'last holiday × (1 + YoY%)',
      tip: BREAKDOWN_TIPS.need,
    },
    {
      key: 'reg',
      label: 'Regular month',
      value: format_cases(row.monthly_sales),
      sub: row.expiry_cover_cases != null ? `+ ${row.expiry_cover_cases} extra cover` : undefined,
      tip: BREAKDOWN_TIPS.regular,
    },
    {
      key: 'exp',
      label: 'Usable shelf',
      value: `${row.expiration_months} months`,
      sub:
        row.shelf_life_days != null
          ? `${row.shelf_life_days} day shelf · ${row.remaining_life_days} days left at holiday`
          : `default · ${row.remaining_life_days} days left at holiday`,
      tip: expiration_tip(row),
    },
    {
      key: 'oh',
      label: 'On hand',
      value: format_cases(row.on_hand_cases),
      tip: BREAKDOWN_TIPS.on_hand,
    },
    {
      key: 'tm',
      label: 'To make',
      value: format_cases(row.to_make_cases),
      tip: BREAKDOWN_TIPS.to_make,
    },
    {
      key: 'win',
      label: 'Window',
      value: `${row.earliest_produce_date || '—'} → ${row.latest_produce_date || '—'}`,
      tip: BREAKDOWN_TIPS.window,
    },
  ];
  return (
    <div className="forecast-breakdown">
      <h3 className="forecast-breakdown__title">
        {row.product_code || 'SKU'} breakdown for {holiday_label}
      </h3>
      <div className="forecast-breakdown__grid">
        {items.map((item) => (
          <div key={item.key} className="forecast-breakdown__item">
            <Tip label={item.label} tip={item.tip} />
            <strong
              className={
                item.key === 'peak' && change_row
                  ? `forecast-anomaly__change forecast-anomaly__change--${peak_direction(change_row)}`
                  : undefined
              }
            >
              {item.value}
            </strong>
            {item.sub && <span className="forecast-breakdown__sub">{item.sub}</span>}
          </div>
        ))}
      </div>
    </div>
  );
}
